import { AssetLibraryType } from '../../enum/dto/asset-library-type.enum';
import { PrintCardMenu } from './print-card-menu';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { Variant } from '../../product/dto/variant';
import { Orientation } from '../../enum/dto/orientation.enum';
import { PrintCardSize } from '../../enum/dto/print-card-size.enum';
import { VariantUtils } from '../../../utils/variant-utils';
import { LocationPriceStream } from '../../enum/shared/location-price-stream';
import type { CardStack } from '../section/card-stacks/card-stack';
import type { CardsThatLayoutOntoPaper } from '../section/cards-that-layout-onto-paper';
import type { SectionWithProducts } from '../section/section-with-products';
import type { SectionRowViewModel } from 'src/app/modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import type { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';

export class GlassHouseBrandGroupPrintCardMenu extends PrintCardMenu {
  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [FONT.Inter.SemiBold_600, FONT.Inter.Bold_700, FONT.Onest.Regular_400, FONT.Onest.SemiBold_600];
  }

  getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/keq5tol.css';
  }

  protected override deserializeSections(): void {
    this.sections = this.splitSectionIntoDifferentCardSizeSections(this.sections?.firstOrNull());
    super.deserializeSections();
  }

  protected override deserializeTemplateSections(): void {
    this.templateSections = this.splitSectionIntoDifferentCardSizeSections(this.templateSections?.firstOrNull());
    super.deserializeTemplateSections();
  }

  /**
   * - this happens before the data is deserialized into CardStack objects, hence the "any" type
   * - 3x4 cards are for products with 1-2 variants
   * - 3x5 index cards are for products with 3 or more variants
   * - divide the products into corresponding card stack sections based on their number of line items
   * - the data coming into here hasn't been deserialized yet, so it's just a plain object
   */
  protected splitSectionIntoDifferentCardSizeSections(section: any): any[] {
    const productsWith1To2Variants = [];
    const productsWithThreeOrMoreVariants = [];
    const enabledVariantsGroupedByLineItem = this.cardWithLineItems(section);
    enabledVariantsGroupedByLineItem?.forEach(card => {
      if (card?.lineItems?.length >= 3) {
        productsWithThreeOrMoreVariants.push({ product: card?.product, variants: card?.lineItems?.flatMap(v => v) });
      } else if (card?.lineItems?.length >= 1) {
        productsWith1To2Variants.push({ product: card?.product, variants: card?.lineItems?.flatMap(v => v) });
      }
    });
    const updatedSections = [];
    if (productsWithThreeOrMoreVariants.length > 0) {
      const products = productsWithThreeOrMoreVariants.map(pv => pv?.product);
      const variants = productsWithThreeOrMoreVariants.flatMap(pv => pv?.variants);
      updatedSections.push(
        this.buildSplitSection(section, products, variants, PrintCardSize.DefaultSize_IndexCard_3x5)
      );
    }
    if (productsWith1To2Variants.length > 0) {
      const products = productsWith1To2Variants.map(pv => pv?.product);
      const variants = productsWith1To2Variants.flatMap(pv => pv?.variants);
      updatedSections.push(this.buildSplitSection(section, products, variants, PrintCardSize.DefaultSize_Custom_3x4));
    }
    return updatedSections;
  }

  /**
   * This happens before the data is deserialized into CardStack objects, hence the "any" type.
   */
  private cardWithLineItems(section: any): { product: any; lineItems: Variant[][] }[] {
    const themeId = this.theme;
    const locId = this.locationId;
    const companyId = this.companyId;
    const priceStream = LocationPriceStream.Default;
    const hideSale = this.menuOptions?.hideSale;
    return (
      section?.products?.map((product: any) => {
        const rawVariantsOnProduct = product?.variants?.filter((variant: any) => {
          return section?.enabledVariantIds.includes(variant.id);
        });
        const enabledVariantOnProduct = window?.injector?.Deserialize?.arrayOf(Variant, rawVariantsOnProduct);
        const variantsAsLineItems = VariantUtils.groupVariantsBySameSizeQuantityAndPrice(
          enabledVariantOnProduct,
          themeId,
          locId,
          companyId,
          priceStream,
          hideSale
        );
        return {
          product: product,
          lineItems: variantsAsLineItems
        };
      }) ?? []
    );
  }

  /**
   * This happens before the data is deserialized into CardStack objects, hence the "any" types.
   */
  protected buildSplitSection(section: any, products: any[], variants: any[], cardSize: PrintCardSize): any {
    return {
      ...section,
      cardSize,
      products,
      productIds: products.map(v => v?.id),
      variants,
      enabledVariantIds: variants.map(v => v?.id)
    };
  }

  protected override setSectionCardSize(isTemplatedMenu: boolean, section: CardStack): void {
    // do nothing, let splitSectionIntoDifferentCardSizeSections handle it
  }

  /**
   * Needed because of 2 card sizes for the menu (out of the normal flow).
   */
  protected override setSectionOrientation(isTemplatedMenu: boolean, section: CardsThatLayoutOntoPaper) {
    section.orientation = Orientation.Portrait;
  }

  /**
   * The glass house theme has 2 card sizes mixed into a single PDF depending on the number of variants a product has.
   * Therefore, we split each section into products based on card size, and then deserialize said section
   * into the correct size. That's why this method returns more than one section.
   */
  override getLayoutInstructions(): CardStack[] {
    return this.sections ?? [];
  }

  override getPriceDecimal(
    priceStream: LocationPriceStream,
    locationId: number,
    s: SectionWithProducts,
    rowVM: SectionRowViewModel,
    colVm: SectionColumnViewModel
  ): string {
    const decimal = super.getPriceDecimal(priceStream, locationId, s, rowVM, colVm);
    return decimal.replace('.', '');
  }

  override shouldFetchVariantAssets(): boolean {
    return true;
  }

  override variantAssetTypeSortOrder(): string[] {
    return [AssetLibraryType.PrimaryBrand, AssetLibraryType.Brand];
  }

  override variantAssetItemCount(): number {
    return 1;
  }
}
